import type { ComponentType } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, ReferenceLine, Tooltip as RTooltip, Legend,
} from 'recharts';
import type { PanelStats } from '../usePanelData';
import type { DashboardPanel } from '../types';
import {
  CHART_COLORS, metricLabel, formatMetric, orderRowsByGroup, chartTokens, axisTickFormatter,
  rowLabel, pivotDims, pivotRows,
} from '../format';

// recharts 2.x class components mistype under this repo's @types/react — alias to loose props.
type AnyProps = Record<string, unknown>;
const RBar = Bar as unknown as ComponentType<AnyProps>;
const RLine = Line as unknown as ComponentType<AnyProps>;
const RPie = Pie as unknown as ComponentType<AnyProps>;
const RCell = Cell as unknown as ComponentType<AnyProps>;
const RLegend = Legend as unknown as ComponentType<AnyProps>;
const RRefLine = ReferenceLine as unknown as ComponentType<AnyProps>;

export default function ChartPanel({ panel, stats }: { panel: DashboardPanel; stats: PanelStats }) {
  const { palette } = useTheme();
  const tok = chartTokens(palette.mode);
  const metrics = panel.query.metrics;
  const groupBy = panel.query.group_by;
  const data = orderRowsByGroup(stats.rows, groupBy).map((r) => {
    const row: Record<string, unknown> = { label: rowLabel(r, groupBy) };
    for (const m of metrics) row[m] = Number(r[m]) || 0;
    return row;
  });

  const tooltipStyle = {
    contentStyle: { background: tok.tooltipBg, border: `1px solid ${tok.grid}`, borderRadius: 8, fontSize: 12 },
    labelStyle: { color: tok.axis }, itemStyle: { padding: 0 },
  };

  if (panel.viz === 'pie') {
    const m = metrics[0];
    const pie = data.map((d) => ({ name: String(d.label), value: Number(d[m]) || 0 }));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <RPie data={pie} dataKey="value" nameKey="name" outerRadius="75%" innerRadius="48%" paddingAngle={1} stroke="none">
            {pie.map((_, i) => <RCell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </RPie>
          <RTooltip {...tooltipStyle} formatter={(v: number) => formatMetric(m, v)} />
          <RLegend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  const Chart = panel.viz === 'bar' ? BarChart : LineChart;
  const Series = panel.viz === 'bar' ? RBar : RLine;

  if (pivotDims(groupBy)) {
    const m = metrics[0];
    const pivot = pivotRows(stats.rows, groupBy, m, stats.series ?? []);
    const chart = (
      <ResponsiveContainer width="100%" height="100%">
        <Chart data={pivot.data} margin={{ top: 6, right: 10, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={tok.grid} vertical={false} />
          <XAxis dataKey="label" tick={{ fill: tok.axis, fontSize: 11 }} tickLine={false} axisLine={{ stroke: tok.grid }} interval="preserveStartEnd" minTickGap={18} />
          <YAxis tickFormatter={axisTickFormatter([m])} tick={{ fill: tok.axis, fontSize: 11 }} tickLine={false} axisLine={false} width={52} />
          <RRefLine y={0} stroke={tok.axis} />
          <RTooltip {...tooltipStyle} formatter={(v: number) => formatMetric(m, v)} />
          <RLegend wrapperStyle={{ fontSize: 11 }} />
          {pivot.series.map((s, i) => (
            <Series key={s.key} type="monotone" dataKey={s.key} name={s.name}
              fill={CHART_COLORS[i % CHART_COLORS.length]} stroke={CHART_COLORS[i % CHART_COLORS.length]}
              strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls radius={panel.viz === 'bar' ? [3, 3, 0, 0] : undefined} />
          ))}
        </Chart>
      </ResponsiveContainer>
    );
    return stats.truncated ? (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="caption" color="warning.main" sx={{ px: 1 }}>
          Partial: over {stats.rows.length} rows — narrow the range or add a filter
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0 }}>{chart}</Box>
      </Box>
    ) : chart;
  }
  // A single-metric bar chart colours each bar by its sign (≥0 blue, <0 red), like the
  // default dashboard's profit-by-hour — a plain magnitude metric stays all-positive/blue.
  const signColored = panel.viz === 'bar' && metrics.length === 1;
  const BAR_POS = '#2a78d6';
  const BAR_NEG = '#d03b3b';
  const y2 = new Set(panel.query.y2_metrics ?? []);
  const leftMetrics = metrics.filter((m) => !y2.has(m));
  const rightMetrics = metrics.filter((m) => y2.has(m));
  const hasRight = rightMetrics.length > 0;
  const leftFmt = axisTickFormatter(leftMetrics);
  const rightFmt = axisTickFormatter(rightMetrics);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <Chart data={data} margin={{ top: 6, right: hasRight ? 4 : 10, bottom: 0, left: 0 }}>
        <CartesianGrid stroke={tok.grid} vertical={false} />
        <XAxis dataKey="label" tick={{ fill: tok.axis, fontSize: 11 }} tickLine={false} axisLine={{ stroke: tok.grid }} interval="preserveStartEnd" minTickGap={18} />
        <YAxis yAxisId="left" tickFormatter={leftFmt} tick={{ fill: tok.axis, fontSize: 11 }} tickLine={false} axisLine={false} width={52} />
        {hasRight && <YAxis yAxisId="right" orientation="right" tickFormatter={rightFmt} tick={{ fill: tok.axis, fontSize: 11 }} tickLine={false} axisLine={false} width={48} />}
        <RRefLine yAxisId="left" y={0} stroke={tok.axis} />
        <RTooltip {...tooltipStyle} formatter={(v: number, n: string) => [formatMetric(n, v), metricLabel(n)]} />
        {metrics.length > 1 && <RLegend wrapperStyle={{ fontSize: 11 }} />}
        {metrics.map((m, i) => (
          <Series
            key={m}
            yAxisId={y2.has(m) ? 'right' : 'left'}
            type="monotone"
            dataKey={m}
            name={metricLabel(m)}
            fill={CHART_COLORS[i % CHART_COLORS.length]}
            stroke={CHART_COLORS[i % CHART_COLORS.length]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            radius={panel.viz === 'bar' ? [3, 3, 0, 0] : undefined}
          >
            {signColored && data.map((d, di) => (
              <RCell key={di} fill={(Number(d[m]) || 0) >= 0 ? BAR_POS : BAR_NEG} />
            ))}
          </Series>
        ))}
      </Chart>
    </ResponsiveContainer>
  );
}
